import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Image,
  Text,
  VStack,
  Heading,
  Tab,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  useToast,
  Button,
  HStack,
  Spacer,
  Link,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
} from "@chakra-ui/react";
import {
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import CountryFlag from "react-country-flag";
import countries from "i18n-iso-countries";
import eng from "i18n-iso-countries/langs/en.json";
import { FaPlane } from "react-icons/fa";

import axiosInstance from "../../helpers/axiosInstance"; // Ensure axiosInstance is correctly imported
import { formatDate } from "../../helpers/utils";

import HotelsCard from "../chat/HotelsCard";
import Activities from "../chat/Activities"; // Ensure the correct import
import FlightCard from "../chat/FlightCard";
import RVRentalCard from "../chat/RVRentalCard";
import BusTicketCard from "../Itinerary/BusTicketCard";
import InfoCard from "../Itinerary/InfoCard";
import ItineraryTabs from "./ItineraryTabs";
import CityTabs from "./CityTabs";
import { useCurrency } from "../CurrencyExchange/CurrencyContext"; // Import useCurrency hook
import { ReactComponent as DonateWhiteIcon } from "../../assets/icons/donate-white.svg";
import Translate from "../../components/LanguageTranslation/Translate";
import { Helmet } from "react-helmet-async";
import CategoryFilterMenu from "../BucketList/CategoryFilterMenu";

import {
  EXPLORE_SUBS,
  ACCOMMODATION_SUBS,
  TRANSPORT_SUBS,
} from "../BucketList/bucketConstants";
import FilterMenu from "./FilterMenu";
import RentalCarCard from "../Itinerary/RentalCarCard";
import OtherCard from "../Itinerary/OtherCard";
import TrainTicketCard from "../Itinerary/TrainTicketCard";

// Register the English locale
countries.registerLocale(eng);

const ManageItinerary = ({
  generalInfo,
  tripId,
  lastUpdated,
  onActivityClick,
  onUpdateClick,
  selectedDayIndex,
  setSelectedDayIndex,
  selectedCityIndex,
  setSelectedCityIndex,
  tripDetails,
  setTripDetails,
  handleHotelUpdateClick,
  handleRVsUpdateClick,
  handleFlightUpdateClick,
  handleAllActivitiesUpdateClick,
  handleSuggestionClick,
  setIsAddActivitiesOpen,
  setIsAddAccommodationOpen,
  setIsAddFlightOpen,
  setIsAddRVOpen,
  setIsAddBusTicketOpen,
  handleDayChange,
  handleBusTicketsUpdateClick,
  handleRentalCarsUpdateClick,
  setIsAddRentalCarOpen,
  handleTrainTicketsUpdateClick,
  setIsAddTrainTicketOpen,
  handleOtherTicketsUpdateClick,
  setIsAddOtherTicketOpen,
}) => {
  // console.log("itinerary", generalInfo);
  const { convertCurrency, currency } = useCurrency();
  const scrollContainerRef = useRef(null);
  const listRef = useRef(null);
  const [totalCost, setTotalCost] = useState(0);
  const [showScrollUp, setShowScrollUp] = useState(false);
  const [accommodationFilter, setAccommodationFilter] = useState(["all"]);
  const [activityFilter, setActivityFilter] = useState(["all"]);
  const [transportationFilter, setTransportationFilter] = useState(["all"]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  // Categories explicitly shown via the header dropdown (does not change filters)
  const [forcedTransportVisibility, setForcedTransportVisibility] = useState([]);

  const selectedDestination =
    generalInfo?.destinations?.[selectedCityIndex] || {};
  const cityTripPlan = selectedDestination.trip_plan || {};
  const cityItinerary = cityTripPlan.itinerary || [];

  const countryName = selectedDestination?.country || "";
  const city = selectedDestination?.city;

  // Filter accommodations based on selected filter
  const filteredAccommodations = React.useMemo(() => {
    if (!Array.isArray(selectedDestination.home_to_go_api_response)) {
      return [];
    }

    if (accommodationFilter.includes("all")) {
      return selectedDestination.home_to_go_api_response;
    }

    if (accommodationFilter.includes("none")) {
      return [];
    }

    return selectedDestination.home_to_go_api_response.filter(
      (accommodation) =>
        accommodation.type && accommodationFilter.includes(accommodation.type)
    );
  }, [selectedDestination.home_to_go_api_response, accommodationFilter]);

  // Filter transportation based on selected filter
  const showFlights = React.useMemo(() => {
    return (
      transportationFilter.includes("all") ||
      transportationFilter.includes("flight") ||
      forcedTransportVisibility.includes("flight")
    );
  }, [transportationFilter, forcedTransportVisibility]);

  const showRVs = React.useMemo(() => {
    return (
      transportationFilter.includes("all") ||
      transportationFilter.includes("rv") ||
      forcedTransportVisibility.includes("rv")
    );
  }, [transportationFilter, forcedTransportVisibility]);

  const showBusTickets = React.useMemo(() => {
    return (
      transportationFilter.includes("all") ||
      transportationFilter.includes("bus") ||
      forcedTransportVisibility.includes("bus")
    );
  }, [transportationFilter, forcedTransportVisibility]);

  const showRentalCars = React.useMemo(() => {
    return (
      transportationFilter.includes("all") ||
      transportationFilter.includes("rental_car") ||
      forcedTransportVisibility.includes("rental_car")
    );
  }, [transportationFilter, forcedTransportVisibility]);

  const showTrainTickets = React.useMemo(() => {
    return (
      transportationFilter.includes("all") ||
      transportationFilter.includes("train") ||
      forcedTransportVisibility.includes("train")
    );
  }, [transportationFilter, forcedTransportVisibility]);

  const showOtherTickets = React.useMemo(() => {
    return (
      transportationFilter.includes("all") ||
      transportationFilter.includes("other") ||
      forcedTransportVisibility.includes("other")
    );
  }, [transportationFilter, forcedTransportVisibility]);

  // Initialize transportation filters only once when component mounts and data is available
  useEffect(() => {
    if (!generalInfo || !selectedDestination || isInitialized) return;
    
    const availablePrefs = [];
    if (Array.isArray(generalInfo?.flight_info) && generalInfo.flight_info.length > 0) availablePrefs.push("flight");
    if (Array.isArray(selectedDestination.outdoorsy_api_response) && selectedDestination.outdoorsy_api_response.length > 0) availablePrefs.push("rv");
    if (Array.isArray(selectedDestination.bus_api_response) && selectedDestination.bus_api_response.length > 0) availablePrefs.push("bus");
    if (Array.isArray(selectedDestination.rental_car_api_response) && selectedDestination.rental_car_api_response.length > 0) availablePrefs.push("rental_car");
    if (Array.isArray(selectedDestination.train_api_response) && selectedDestination.train_api_response.length > 0) availablePrefs.push("train");
    if (Array.isArray(selectedDestination.other_transportation_api_response) && selectedDestination.other_transportation_api_response.length > 0) availablePrefs.push("other");
    
    if (availablePrefs.length === 0) {
      setTransportationFilter(["none"]);
    } else if (availablePrefs.length === 6) {
      setTransportationFilter(["all"]);
    } else {
      setTransportationFilter(availablePrefs);
    }
    
    setIsInitialized(true);
  }, [generalInfo, selectedDestination, isInitialized]); // Trigger when data is available

  // Reset filters when city changes only
  useEffect(() => {
    setAccommodationFilter(["all"]);
    setActivityFilter(["all"]);
    setIsInitialized(false); // Reset initialization flag for new city
    
    // --- Begin: Auto-uncheck unavailable transportation subcategories ---
    const availablePrefs = [];
    if (Array.isArray(generalInfo?.flight_info) && generalInfo.flight_info.length > 0) availablePrefs.push("flight");
    if (Array.isArray(selectedDestination.outdoorsy_api_response) && selectedDestination.outdoorsy_api_response.length > 0) availablePrefs.push("rv");
    if (Array.isArray(selectedDestination.bus_api_response) && selectedDestination.bus_api_response.length > 0) availablePrefs.push("bus");
    if (Array.isArray(selectedDestination.rental_car_api_response) && selectedDestination.rental_car_api_response.length > 0) availablePrefs.push("rental_car");
    if (Array.isArray(selectedDestination.train_api_response) && selectedDestination.train_api_response.length > 0) availablePrefs.push("train");
    if (Array.isArray(selectedDestination.other_transportation_api_response) && selectedDestination.other_transportation_api_response.length > 0) availablePrefs.push("other");
    
    // Keep existing filters for transportation types that have data, only reset those without data
    const currentFilters = transportationFilter.filter(filter => 
      filter !== "all" && filter !== "none"
    );
    
    // Preserve filters for transportation types that have data
    const preservedFilters = currentFilters.filter(filter => 
      availablePrefs.includes(filter)
    );
    
    if (availablePrefs.length === 0) {
      setTransportationFilter(["none"]);
    } else if (availablePrefs.length === 6) {
      setTransportationFilter(["all"]);
    } else {
      // Combine preserved filters with available preferences
      const newFilters = [...new Set([...preservedFilters, ...availablePrefs])];
      setTransportationFilter(newFilters);
    }
    // --- End: Auto-uncheck unavailable transportation subcategories ---
  }, [selectedCityIndex]); // Only trigger when city changes, not when data updates

  const countryFlagLink = (countryName) => {
    const countryCode = countryName
      ? countries.getAlpha2Code(countryName, "en")
      : null;
    if (!countryCode) {
      return "../assets/images/banner.jpg";
    }
    return `https://flagcdn.com/w320/${countryCode.toLowerCase()}.png`;
  };

  const firstDayImage =
    cityItinerary[0]?.activity_detail_list?.[0]?.images?.[0]?.variants?.[2]
      ?.url;
  const firstImageSrc =
    firstDayImage || countryFlagLink(selectedDestination?.country);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const suffix =
      day === 1 || day === 21 || day === 31
        ? "st"
        : day === 2 || day === 22
        ? "nd"
        : day === 3 || day === 23
        ? "rd"
        : "th";

    return date
      .toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
      .replace(day.toString(), `${day}${suffix}`);
  };

  // Function to format route with plane icon
  const formatRoute = (route) => {
    if (!route) return "";
    if (route.includes("--> ")) {
      const parts = route.split("--> ");
      return (
        <HStack spacing={2} display="inline-flex">
          <Text mb={0}>{parts[0]}</Text>
          <FaPlane />
          <Text mb={0}>{parts[1]}</Text>
        </HStack>
      );
    }
    return route;
  };

  /* ---------- NEW state + ref ---------- */

  /* Show button only after scrolling ~250 px */
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const onScroll = () => setShowScrollUp(el.scrollTop > 250);
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () =>
    listRef.current?.scrollTo({ top: 0, behavior: "smooth" });

  // Ensure tripDetails is not null and has a country property before accessing it
  const countryCode = countryName
    ? countries.getAlpha2Code(countryName, "en")
    : null;

  useEffect(() => {
    if (!tripId) return;
    const fetchExpenses = async () => {
      try {
        const response = await axiosInstance.get(
          `/budget_planner/${tripId}/expenses`
        );
        const expensesData = response.data;

        if (Array.isArray(expensesData)) {
          // Calculate the total cost across all categories
          const total = expensesData.reduce((acc, category) => {
            if (category.expenses && Array.isArray(category.expenses)) {
              const categoryTotal = category.expenses.reduce(
                (catAcc, expense) => {
                  return catAcc + (expense.cost || 0); // Default to 0 if cost is undefined
                },
                0
              );
              return acc + categoryTotal;
            } else {
              console.warn("Expenses is not an array for category:", category);
            }
            return acc;
          }, 0);

          setTotalCost(total); // Set the total cost in the state
        } else {
          console.error("Response data is not an array");
        }
      } catch (error) {
        console.error("Error fetching expenses:", error);
      }
    };

    fetchExpenses();
  }, [tripId]);

  // Convert the total cost to the selected currency
  const convertedTotalCost = parseFloat(convertCurrency(totalCost, "USD")) || 0;

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMenuOpen]);

  if (!generalInfo) {
    return null;
  }

  const handleUpdateClickInternal = () => {
    onUpdateClick(); // Trigger the parent's update function
  };

  const handleHotelUpdateClickInternal = () => {
    handleHotelUpdateClick(); // Trigger the parent's update function
  };

  const handleRVsUpdateClickInternal = () => {
    handleRVsUpdateClick(); // Trigger the parent's update function
  };

  const handleFlightUpdateClickInternal = () => {
    handleFlightUpdateClick(); // Trigger the parent's update function
  };

  const handleActivitiesUpdateClickInternal = () => {
    handleAllActivitiesUpdateClick(); // Trigger the parent's update function
  };

  const handleBusTicketsUpdateClickInternal = () => {
    handleBusTicketsUpdateClick();
  };

  const handleRentalCarsUpdateClickInternal = () => {
    handleRentalCarsUpdateClick();
  };

  // Select and reveal a specific transportation category from the header dropdown (without changing filters)
  const selectTransportationCategory = (category) => {
    setForcedTransportVisibility((prev) =>
      prev.includes(category) ? prev : [...prev, category]
    );
  };

  // Build dropdown options for categories that are currently hidden by filters (and not already forced visible)
  const hiddenTransportCategories = [
    { key: "flight", label: "Update Flights" },
    { key: "rv", label: "Update RVs" },
    { key: "bus", label: "Update Bus Tickets" },
    { key: "rental_car", label: "Update Rental Cars" },
    { key: "train", label: "Update Train Tickets" },
    { key: "other", label: "Update Other Transportation" },
  ].filter((item) => {
    switch (item.key) {
      case "flight":
        return !showFlights;
      case "rv":
        return !showRVs;
      case "bus":
        return !showBusTickets;
      case "rental_car":
        return !showRentalCars;
      case "train":
        return !showTrainTickets;
      case "other":
        return !showOtherTickets;
      default:
        return false;
    }
  });

  return (
    <>
      <Helmet>
        <title>{generalInfo?.itinerary?.trip_name}</title>
      </Helmet>

      <Box
        ref={listRef}
        right="0"
        top="0"
        h={{ base: "80vh", md: "92vh" }}
        maxW="99vw"
        p={{ base: 2, md: 5 }}
        pt={{ base: 2, md: 2 }}
        bg="white"
        zIndex="modal"
        overflowY={{ base: "auto", md: "auto" }}
      >
        <VStack spacing={{ base: 4, md: 2 }} align="stretch" pt="2">
          <Image
            src={firstImageSrc}
            alt={selectedDestination?.city || "No Description Available"}
            borderRadius="md"
            boxSize="100%"
            objectFit="cover"
            mb={0}
            maxH={{ base: "200px", md: "300px" }}
          />
          <VStack
            spacing={4}
            alignItems="flex-start"
            justifyContent="flex-start"
            mt={{ base: 2, md: 5 }}
          >
            <Text fontSize={{ base: "xs", md: "sm" }} mb={0}>
              <Translate
                text={`${
                  generalInfo?.departure_date
                    ? formatDate(generalInfo.departure_date)
                    : "N/A"
                } to ${
                  generalInfo?.return_date
                    ? formatDate(generalInfo.return_date)
                    : "N/A"
                }`}
              />
            </Text>

            <Heading size={{ base: "sm", md: "md" }} mb={0}>
              <Translate
                text={generalInfo?.itinerary?.trip_name || "No Title Available"}
              />
              {countryCode ? (
                <CountryFlag
                  countryCode={countryCode}
                  svg
                  style={{
                    width: "1.25em",
                    height: "1.25em",
                    marginLeft: "0.5em",
                    boxShadow:
                      "-4px 0 4px -2px rgba(0,0,0,0.1), 4px 0 4px -2px rgba(0,0,0,0.1)", // Shadows on left and right
                    backgroundColor: "#f0f0f0", // Light gray background
                    borderRadius: "4px", // Optional: rounded corners
                  }}
                />
              ) : (
                <p></p>
              )}
            </Heading>
            {/* Display converted total cost */}
            <Link
              as={RouterLink}
              to="/ai-travel-planner/budget-planner"
              fontSize={{ base: "xs", md: "sm" }}
              mb={0}
            >
              {currency}{" "}
              {convertedTotalCost ? Math.round(convertedTotalCost) : "N/A"}
            </Link>

            <Text size={{ base: "sm", md: "md" }} mb={0}>
              <Translate
                text={
                  generalInfo?.itinerary?.trip_description ||
                  "No Description Available"
                }
              />
            </Text>
            <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500" mb={0}>
              <Translate
                text={`Last updated: ${
                  lastUpdated ? formatDate(lastUpdated.split(" ")[0]) : "N/A"
                }`}
              />
            </Text>
          </VStack>
          <InfoCard
            tripId={tripId}
            info={generalInfo}
            city={city}
            country={countryName}
            currency={currency}
          />
          {/* City Tabs */}
          <CityTabs
            destinations={generalInfo?.destinations}
            selectedCityIndex={selectedCityIndex}
            setSelectedCityIndex={setSelectedCityIndex}
            countryFlagLink={countryFlagLink}
          />

          {/* Itinerary */}
          <ItineraryTabs
            generalInfo={generalInfo}
            cityItinerary={cityItinerary}
            selectedDayIndex={selectedDayIndex}
            setSelectedDayIndex={setSelectedDayIndex}
            tripId={tripId}
            cityName={selectedDestination.city}
            handleUpdateClickInternal={handleUpdateClickInternal}
            handleSuggestionClick={handleSuggestionClick}
            setIsAddActivitiesOpen={setIsAddActivitiesOpen}
            city={city}
            fromItineray={true}
            handleDayChange={handleDayChange}
            selectedCityIndex={selectedCityIndex}
            setSelectedCityIndex={setSelectedCityIndex}
          />
        </VStack>

        <Box>
          <HStack
            spacing={0}
            align="center"
            justifyContent="space-between"
            mt={6}
            mb={0}
            flexDirection={{ base: "row", md: "row" }}
          >
            <Heading fontSize={{ base: "sm", md: "md", lg: "xl" }} mb={0}>
              <Translate text="Accommodations" />
            </Heading>
            {Array.isArray(selectedDestination.home_to_go_api_response) &&
              selectedDestination.home_to_go_api_response.length > 0 && (
                <FilterMenu
                  filterOptions={ACCOMMODATION_SUBS}
                  selectedFilter={accommodationFilter}
                  onFilterChange={setAccommodationFilter}
                  multiple={true}
                />
              )}
            <Spacer />
            <Menu
              onOpen={() => setIsMenuOpen(true)}
              onClose={() => setIsMenuOpen(false)}
            >
              <MenuButton
                as={Button}
                aria-label="Update hotels"
                bg="#1A202C"
                color="white"
                _hover="none"
                _active="none"
                size={{ base: "xs", md: "xs", lg: "sm" }}
                fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                rightIcon={<ChevronDownIcon />}
                onClick={handleHotelUpdateClickInternal}
              >
                <Translate text="Update Accommodations" />
              </MenuButton>
              <MenuList>
                <MenuItem
                  onClick={() => setIsAddAccommodationOpen(true)}
                  fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                >
                  <Translate text="Add Bucket List Items" />
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    handleSuggestionClick(
                      `Could you give me more recommendation for accomodations in ${selectedDestination?.city}`,
                      "accommodation_conversation"
                    )
                  }
                  fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                >
                  <Translate text="Get more Recommendations" />
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>

          {Array.isArray(selectedDestination.home_to_go_api_response) &&
          selectedDestination.home_to_go_api_response.length > 0 ? (
            <HotelsCard
              hotels={filteredAccommodations}
              onActivityClick={onActivityClick}
              fromItineray={true}
            />
          ) : (
            <Text fontSize="sm" color="gray.500" mt={2}>
              <Translate text="No accommodations created yet" />
            </Text>
          )}
        </Box>

        <Box>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            mt={4}
            mb={0}
          >
            <HStack spacing={2}>
              <Heading
                spacing={0}
                fontSize={{ base: "sm", md: "md", lg: "xl" }}
                mb={0}
              >
                <Translate text="Transportation" />
              </Heading>
              {(Array.isArray(generalInfo?.flight_info) &&
                generalInfo.flight_info.length > 0) ||
              (Array.isArray(selectedDestination.outdoorsy_api_response) &&
                selectedDestination.outdoorsy_api_response.length > 0) ? (
                <FilterMenu
                  filterOptions={TRANSPORT_SUBS}
                  selectedFilter={transportationFilter}
                  onFilterChange={setTransportationFilter}
                  multiple={true}
                />
              ) : null}
            </HStack>
            <Spacer />
            {hiddenTransportCategories.length > 0 && (
              <Box mt={{ base: 2, md: 0 }}>
                <Menu placement="bottom-end" onOpen={() => setIsMenuOpen(true)} onClose={() => setIsMenuOpen(false)}>
                  <MenuButton
                    as={Button}
                    aria-label="Update Transportation"
                    bg="#1A202C"
                    color="white"
                    _hover="none"
                    _active="none"
                    size={{ base: "xs", md: "xs", lg: "sm" }}
                    fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    rightIcon={<ChevronDownIcon />}
                  >
                    <Translate text="Update Categories" />
                  </MenuButton>
                  <MenuList>
                    {hiddenTransportCategories.map((item) => (
                      <MenuItem
                        key={item.key}
                        onClick={() => selectTransportationCategory(item.key)}
                        fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                      >
                        <Translate text={item.label} />
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </Box>
            )}
          </Flex>

          {showFlights && (
            <Box>
              <HStack
                align="center"
                justifyContent="space-between"
                mt={2}
                mb={0}
                mr={{ base: 1, md: 0, lg: 0 }}
                flexDirection={{ base: "row", md: "row" }}
              >
                <Heading
                  mb={0}
                  fontSize={{ base: "sm", md: "md", lg: "xl" }}
                  color="gray.500"
                >
                  <Translate text="Flights" />
                </Heading>
                <Menu
                  onOpen={() => setIsMenuOpen(true)}
                  onClose={() => setIsMenuOpen(false)}
                >
                  <MenuButton
                    as={Button}
                    aria-label="Update flight"
                    bg="#1A202C"
                    color="white"
                    _hover="none"
                    _active="none"
                    size={{ base: "xs", md: "xs", lg: "sm" }}
                    fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    rightIcon={<ChevronDownIcon />}
                    onClick={handleFlightUpdateClickInternal}
                  >
                    <Translate text="Update Flights" />
                  </MenuButton>
                  <MenuList>
                    <MenuItem
                      onClick={() => setIsAddFlightOpen(true)}
                      fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    >
                      <Translate text="Add Bucket List Items" />
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        handleSuggestionClick(
                          `Could you give me more recommendation for flights in ${selectedDestination?.city}`,
                          "transportation_conversation"
                        )
                      }
                      fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    >
                      <Translate text="Get more Recommendations" />
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
              {Array.isArray(generalInfo?.flight_info) &&
              generalInfo.flight_info.length > 0 ? (
                generalInfo.flight_info.map((item, idx) => (
                  <Box key={idx} mb={4}>
                    <Heading fontSize="16px" fontFamily="Arial" mt={2} mb={0}>
                      {formatRoute(item.route)}
                    </Heading>
                    <FlightCard data={item.flights} fromItineray={true} />
                  </Box>
                ))
              ) : (
                <Text fontSize="sm" color="gray.500" mt={2}>
                  <Translate text="No flights created yet" />
                </Text>
              )}
            </Box>
          )}

          {showRVs && (
            <Box>
              <HStack
                align="center"
                justifyContent="space-between"
                mt={4}
                mb={0}
                mr={{ base: 1, md: 0, lg: 0 }}
                flexDirection={{ base: "row", md: "row" }}
              >
                <Heading
                  fontSize={{ base: "sm", md: "md", lg: "xl" }}
                  color="gray.500"
                >
                  <Translate text="RVs for Rental" />
                </Heading>
                <Menu
                  onOpen={() => setIsMenuOpen(true)}
                  onClose={() => setIsMenuOpen(false)}
                >
                  <MenuButton
                    as={Button}
                    aria-label="Update RVs"
                    bg="#1A202C"
                    color="white"
                    _hover="none"
                    _active="none"
                    size={{ base: "xs", md: "xs", lg: "sm" }}
                    fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    rightIcon={<ChevronDownIcon />}
                    onClick={handleRVsUpdateClickInternal}
                  >
                    <Translate text="Update RVs" />
                  </MenuButton>
                  <MenuList>
                    <MenuItem
                      onClick={() => setIsAddRVOpen(true)}
                      fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    >
                      <Translate text="Add Bucket List Items" />
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        handleSuggestionClick(
                          `Could you give me more recommendation for RVs in ${selectedDestination?.city}`,
                          "transportation_conversation"
                        )
                      }
                      fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    >
                      <Translate text="Get more Recommendations" />
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
              {Array.isArray(selectedDestination.outdoorsy_api_response) &&
              selectedDestination.outdoorsy_api_response.length > 0 ? (
                <RVRentalCard
                  RVs={selectedDestination.outdoorsy_api_response}
                  onActivityClick={onActivityClick}
                  fromItineray={true}
                />
              ) : (
                <Text fontSize="sm" color="gray.500" mt={2}>
                  <Translate text="No RVs." />
                </Text>
              )}
            </Box>
          )}

          {showBusTickets && (
            <Box>
              <HStack
                align="center"
                justifyContent="space-between"
                mt={4}
                mb={0}
                mr={{ base: 1, md: 0, lg: 0 }}
                flexDirection={{ base: "row", md: "row" }}
              >
                <Heading
                  fontSize={{ base: "sm", md: "md", lg: "xl" }}
                  color="gray.500"
                >
                  <Translate text="Bus Tickets" />
                </Heading>
                <Menu
                  onOpen={() => setIsMenuOpen(true)}
                  onClose={() => setIsMenuOpen(false)}
                >
                  <MenuButton
                    as={Button}
                    aria-label="Update Bus Tickets"
                    bg="#1A202C"
                    color="white"
                    _hover="none"
                    _active="none"
                    size={{ base: "xs", md: "xs", lg: "sm" }}
                    fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    rightIcon={<ChevronDownIcon />}
                    onClick={handleBusTicketsUpdateClickInternal}
                  >
                    <Translate text="Update Bus Tickets" />
                  </MenuButton>
                  <MenuList>
                    <MenuItem
                      onClick={() => setIsAddBusTicketOpen(true)}
                      fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    >
                      <Translate text="Add Bucket List Items" />
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
              {Array.isArray(selectedDestination.bus_api_response) &&
              selectedDestination.bus_api_response.length > 0 ? (
                <BusTicketCard
                  busTickets={selectedDestination.bus_api_response}
                />
              ) : (
                <Text fontSize="sm" color="gray.500" mt={2}>
                  <Translate text="No Bus Tickets." />
                </Text>
              )}
            </Box>
          )}

          {showRentalCars && (
            <Box>
              <HStack
                align="center"
                justifyContent="space-between"
                mt={4}
                mb={0}
                mr={{ base: 1, md: 0, lg: 0 }}
                flexDirection={{ base: "row", md: "row" }}
              >
                <Heading
                  fontSize={{ base: "sm", md: "md", lg: "xl" }}
                  color="gray.500"
                >
                  <Translate text="Rental Cars" />
                </Heading>
                <Menu
                  onOpen={() => setIsMenuOpen(true)}
                  onClose={() => setIsMenuOpen(false)}
                >
                  <MenuButton
                    as={Button}
                    aria-label="Update Rental Cars"
                    bg="#1A202C"
                    color="white"
                    _hover="none"
                    _active="none"
                    size={{ base: "xs", md: "xs", lg: "sm" }}
                    fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    rightIcon={<ChevronDownIcon />}
                    onClick={handleRentalCarsUpdateClickInternal}
                  >
                    <Translate text="Update Rental Cars" />
                  </MenuButton>
                  <MenuList>
                    <MenuItem
                      onClick={() => setIsAddRentalCarOpen(true)}
                      fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    >
                      <Translate text="Add Bucket List Items" />
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
              {Array.isArray(selectedDestination.rental_car_api_response) &&
              selectedDestination.rental_car_api_response.length > 0 ? (
                <RentalCarCard
                  rentalCars={selectedDestination.rental_car_api_response}
                />
              ) : (
                <Text fontSize="sm" color="gray.500" mt={2}>
                  <Translate text="No Rental Cars." />
                </Text>
              )}
            </Box>
          )}
          
          {/* Train Tickets Section */}
          {showTrainTickets && (
            <Box>
              <HStack
                align="center"
                justifyContent="space-between"
                mt={4}
                mb={0}
                mr={{ base: 1, md: 0, lg: 0 }}
                flexDirection={{ base: "row", md: "row" }}
              >
                <Heading
                  fontSize={{ base: "sm", md: "md", lg: "xl" }}
                  color="gray.500"
                  mb={0}
                >
                  <Translate text="Train Tickets" />
                </Heading>
                <Menu
                  onOpen={() => setIsMenuOpen(true)}
                  onClose={() => setIsMenuOpen(false)}
                >
                  <MenuButton
                    as={Button}
                    aria-label="Update Train Tickets"
                    bg="#1A202C"
                    color="white"
                    _hover="none"
                    _active="none"
                    size={{ base: "xs", md: "xs", lg: "sm" }}
                    fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    rightIcon={<ChevronDownIcon />}
                    onClick={handleTrainTicketsUpdateClick}
                  >
                    <Translate text="Update Train Tickets" />
                  </MenuButton>
                  <MenuList>
                    <MenuItem
                      onClick={() => setIsAddTrainTicketOpen(true)}
                      fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    >
                      <Translate text="Add Bucket List Items" />
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
              {Array.isArray(selectedDestination.train_api_response) &&
              selectedDestination.train_api_response.length > 0 ? (
                <TrainTicketCard
                  trainTickets={selectedDestination.train_api_response}
                />
              ) : (
                <Text fontSize="sm" color="gray.500" mt={2}>
                  <Translate text="No Train Tickets." />
                </Text>
              )}
            </Box>
          )}

          {/* Other Tickets Section */}
          {showOtherTickets && (
            <Box>
              <HStack
                align="center"
                justifyContent="space-between"
                mt={4}
                mb={0}
                mr={{ base: 1, md: 0, lg: 0 }}
                flexDirection={{ base: "row", md: "row" }}
              >
                <Heading
                  fontSize={{ base: "sm", md: "md", lg: "xl" }}
                  color="gray.500"
                  mb={0}
                >
                  <Translate text="Other Transportation" />
                </Heading>
                <Menu
                  onOpen={() => setIsMenuOpen(true)}
                  onClose={() => setIsMenuOpen(false)}
                >
                  <MenuButton
                    as={Button}
                    aria-label="Update Other Transportation"
                    bg="#1A202C"
                    color="white"
                    _hover="none"
                    _active="none"
                    size={{ base: "xs", md: "xs", lg: "sm" }}
                    fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    rightIcon={<ChevronDownIcon />}
                    onClick={handleOtherTicketsUpdateClick}
                  >
                    <Translate text="Update Other Transportation" />
                  </MenuButton>
                  <MenuList>
                    <MenuItem
                      onClick={() => setIsAddOtherTicketOpen(true)}
                      fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                    >
                      <Translate text="Add Bucket List Items" />
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
              {Array.isArray(selectedDestination.other_transportation_api_response) &&
              selectedDestination.other_transportation_api_response.length > 0 ? (
                <OtherCard
                  otherTickets={selectedDestination.other_transportation_api_response}
                />
              ) : (
                <Text fontSize="sm" color="gray.500" mt={2}>
                  <Translate text="No Other Transportation." />
                </Text>
              )}
            </Box>
          )}
        </Box>

        <HStack
          align="left"
          justifyContent="space-between"
          mt={4}
          mb={0}
          flexDirection={{ base: "column", md: "column" }}
        >
          <Text fontSize={{ base: "sm", md: "sm", lg: "sm", xl: "md" }} mb={0}>
            <Translate text="Make a difference with your contribution. Offset your CO2 emissions today and help build a more sustainable future." />
          </Text>
          <Button
            leftIcon={<DonateWhiteIcon width="24px" height="24px" />}
            as={RouterLink}
            to="/offset-your-carbon-footprint"
            bg="#286328"
            color="white"
            size="sm"
            _hover="none"
            width={{ base: "50%", md: "30%" }}
          >
            <Translate text="Contribute" />
          </Button>
        </HStack>
        {/* ② Scroll‑up button */}
        {showScrollUp && (
          <IconButton
            icon={<ArrowUpIcon boxSize={5} />}
            position="fixed"
            bottom={{ base: 16, md: 6 }}
            right={{ base: 4, md: 8 }}
            size="md"
            bg="#1A202C"
            color="white"
            _hover={{ bg: "#1A202C" }}
            borderRadius="full"
            zIndex="1200"
            onClick={scrollToTop}
          />
        )}
      </Box>
    </>
  );
};

export default ManageItinerary;
